import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import NotFoundPage from "./NotFound";
import { ArrowLeft, CheckCircle, Clock } from "lucide-react";
import { CTABanner } from "../components/Shared";
import { BLOG_POSTS } from "../lib/blogPosts";
import { FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const Section = ({ section }) => (
  <div className="mb-8">
    <h2 className="text-xl md:text-2xl font-bold mb-4" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
      {section.h}
    </h2>
    {section.p?.map((para) => (
      <p key={para.slice(0, 40)} className="text-base leading-relaxed mb-4" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>
        {para}
      </p>
    ))}
    {section.list && (
      <div className="space-y-3 mt-2">
        {section.list.map((item) => (
          <div key={item} className="flex items-start gap-3">
            <CheckCircle size={17} style={{ color: GOLD, flexShrink: 0, marginTop: "3px" }} />
            <span className="text-base" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>
              {item}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const PostHero = ({ post }) => (
  <div className="relative h-72 md:h-96 overflow-hidden">
    <img src={post.image} alt={`${post.title} — Cindyrella Cleaning blog, Naples FL`} className="w-full h-full object-cover" />
    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(27,58,107,0.85), rgba(27,58,107,0.25))" }} />
    <div className="absolute bottom-0 left-0 right-0">
      <div className="max-w-3xl mx-auto px-4 md:px-8 pb-8">
        <div className="flex items-center gap-3 mb-3 text-xs" style={{ fontFamily: FONT_BODY }}>
          <span className="font-bold uppercase tracking-widest" style={{ color: GOLD }}>{post.date}</span>
          <span className="flex items-center gap-1 text-white/80">
            <Clock size={12} />
            {post.readTime}
          </span>
        </div>
        <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight" style={{ fontFamily: FONT_HEAD }} data-testid="blog-post-title">
          {post.title}
        </h1>
      </div>
    </div>
  </div>
);

const fetchPost = (slug) =>
  axios
    .get(`${process.env.REACT_APP_BACKEND_URL}/api/blog/${slug}`)
    .then((res) => res.data)
    .catch(() => null);

const useBlogPost = (slug) => {
  const staticPost = BLOG_POSTS.find((p) => p.slug === slug);
  const [post, setPost] = useState(staticPost);
  const [notFound, setNotFound] = useState(false);
  const hasStatic = !!staticPost;

  useEffect(() => {
    fetchPost(slug).then((data) => (data ? setPost(data) : !hasStatic && setNotFound(true)));
  }, [slug, hasStatic, setPost, setNotFound]);

  useEffect(() => {
    if (post) document.title = `${post.title} | Cindyrella Cleaning LLC`;
    return () => {
      document.title = "Cindyrella Cleaning LLC | Naples, FL";
    };
  }, [post]);

  return { post, notFound };
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const { post, notFound } = useBlogPost(slug);

  if (notFound) return <NotFoundPage />;
  if (!post)
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-post-loading">
        <span className="text-sm" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>Loading…</span>
      </div>
    );

  return (
    <>
      <article data-testid="blog-post-page" className="pt-16 md:pt-20">
        <PostHero post={post} />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 mb-8 text-sm font-bold transition-colors hover:text-[#D4A843]"
            style={{ color: NAVY, fontFamily: FONT_BODY }}
          >
            <ArrowLeft size={15} />
            All Articles
          </Link>
          {post.sections.map((s) => (
            <Section key={s.h} section={s} />
          ))}
        </div>
      </article>
      <CTABanner testid="blog-post-cta-banner" />
    </>
  );
}
